import type { FC } from "react";
import { twMerge } from "tailwind-merge";

import type { SectionType } from "components/server-types";
import type { LargeSizeType } from "mds/global-types";
import {
  BookSectionIcon,
  BookSectionLinkedIcon,
  BookSectionTemplateIcon,
  EyeNotVisibleIcon,
} from "mds/icons";
import { useCapabilities, useHomeRoleCapabilities } from "store/capability-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isTemplateSection, isUnlinkedSection } from "utils/publishing";
import { useViewLevel } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

type SectionIconProps = {
  // TODO: remove section and pass a sectionId instead
  section: Pick<SectionType, "kind" | "term_id" | "template_id" | "archived_at">;
  size?: LargeSizeType;
  className?: string;
  preferStaffIcons?: boolean;
};

export const SectionIcon: FC<SectionIconProps> = ({
  section,
  size = "m",
  className,
  preferStaffIcons = false,
}) => {
  const { isArchived } = useIsArchived();
  const ccmEnabled = isCCMEnabled();

  // This component is used in the home view for which we need to check the home role capabilities.
  // For other views, we use the capabilities from the store.
  const viewLevel = useViewLevel();
  const homeRoleCapabilities = useHomeRoleCapabilities();
  const capabilities = useCapabilities();
  const canParticipate = viewLevel.home
    ? homeRoleCapabilities.canParticipateInSection
    : capabilities.canParticipateInSection;

  if (isArchived(section)) return <EyeNotVisibleIcon size={size} />;

  if (isUnlinkedSection(section) || (canParticipate && !preferStaffIcons) || !ccmEnabled) {
    return (
      <span className={twMerge("text-black-tint-40", className)}>
        <BookSectionIcon size={size} />
      </span>
    );
  }

  if (isTemplateSection(section))
    return (
      <span className={twMerge("text-hibiscus-tint-40", className)}>
        <BookSectionTemplateIcon size={size} />
      </span>
    );

  return (
    <span className={twMerge("text-moss-tint-30", className)}>
      <BookSectionLinkedIcon size={size} />
    </span>
  );
};
